/*
 * Ascending Pets: case code capture + checkout gate (Dawn 16).
 * - Reads ?case= on every page (param name is case-insensitive), trims, uppercases, validates.
 * - Validation mode 'metaobject' (default) fetches /pages/case/<handle> (metaobject web page)
 *   and requires data-case-status="active"; 'format-only' checks the format only.
 *   <handle> is NOT the code (metaobject pages are listed in sitemap.xml): it is
 *   'c-' + first 16 hex chars of SHA-256('ascendingpets-case:' + CODE), via crypto.subtle.
 *   Without crypto.subtle (non-HTTPS / very old browser) a well-formed code is accepted as 'unverified'.
 * - Saves cart attributes: "Case code", "Case code source" (link|typed), "Case code check",
 *   and (gate) "Pet name". Keeps a localStorage copy and re-applies it to new/empty carts.
 * - <ap-case-gate> (cart page + drawer) blocks checkout until code is valid and pet name is saved.
 */
(() => {
  if (window.APCase) return;

  const cfgEl = document.getElementById('ap-case-config');
  let parsed = {};
  try {
    parsed = cfgEl ? JSON.parse(cfgEl.textContent) : {};
  } catch (e) {
    parsed = {};
  }
  const cfg = Object.assign(
    {
      mode: 'metaobject',
      phone: '385-600-6177',
      sms: '+13856006177',
      root: '/',
      cartUrl: '/cart',
      urlHandle: 'case',
      storageDays: 30,
      checkTtlMinutes: 10,
    },
    parsed
  );
  const ROOT = String(cfg.root || '/').replace(/\/?$/, '/');
  const CODE_RE = /^AP-[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{5}$/;
  const DEMO = 'AP-DEMO';
  const ATTR = { code: 'Case code', source: 'Case code source', check: 'Case code check', pet: 'Pet name', path: 'Landing path' };
  const LS_KEY = 'ap_case_v1';
  const SS_KEY = 'ap_case_checks_v1';
  const PET_MAX = 60;
  // Must match tools/gen_case_codes.py, tools/case-handle.html, the CASEHANDLE sheet function and Flow.
  const HANDLE_SALT = 'ascendingpets-case:';
  const HANDLE_HEX = 16;

  // The care-team number appears ONLY in these case code messages (owner decision). It is rendered
  // as an sms: link by renderMsg(); text-only messages stay plain.
  const PHONE = String(cfg.phone || '').trim();
  const SMS = String(cfg.sms || '').replace(/[^+\d]/g, '');
  // Owner-approved care-team sentence (exact wording): "Text the Ascending Pets care team at 385-600-6177"
  const CARE = PHONE ? `Text the Ascending Pets care team at ${PHONE}` : 'Text the Ascending Pets care team';
  const MSG = {
    format: `That code doesn't look quite right. Codes look like AP-7K3Q9 (letters and numbers, no 0, O, 1, I or L). ${CARE}`,
    unknown: `We couldn't find that code. Please check your paperwork. ${CARE}`,
    closed: `That case is no longer open for orders. ${CARE}`,
    checking: 'Checking your code…',
    found: 'Thank you, we found your case.',
    unverified: "We couldn't check your code just now. You can continue, and we'll confirm it with you.",
    petMissing: "Please add your pet's name so we can match your order.",
    codeMissing: 'Please add the case code from your paperwork.',
    saveError: `Something went wrong saving your details. Please try again. ${CARE}`,
    linkProblem: `We couldn't confirm the case code in your link. You can enter it before checkout. ${CARE}`,
  };

  /** Put a message into el; the phone number (if present) becomes an sms: link. Never uses innerHTML. */
  function renderMsg(el, text) {
    if (!el) return;
    const t = text || '';
    el.textContent = '';
    const i = PHONE && SMS ? t.indexOf(PHONE) : -1;
    if (i < 0) {
      el.textContent = t;
      return;
    }
    const a = document.createElement('a');
    a.className = 'ap-case-sms';
    a.href = 'sms:' + SMS;
    a.textContent = PHONE;
    el.append(t.slice(0, i), a, t.slice(i + PHONE.length));
  }

  /* ---------- helpers ---------- */
  function normalize(raw) {
    if (raw == null) return '';
    let s = String(raw)
      .trim()
      .toUpperCase()
      .replace(/[\u2010-\u2015\u2212]/g, '-')
      .replace(/\s+/g, '');
    const m = s.match(/^AP-?(.+)$/);
    if (m) s = 'AP-' + m[1];
    return s;
  }
  function isFormatValid(code) {
    return code === DEMO || CODE_RE.test(code);
  }
  function cleanPet(raw) {
    return String(raw || '')
      .replace(/[|\s]+/g, ' ')
      .trim()
      .slice(0, PET_MAX);
  }
  function normalizePath(raw) {
    const p = String(raw || '').trim().toLowerCase();
    return p === 'clinic' ? 'clinic' : 'home';
  }
  function applyPath(raw) {
    const path = normalizePath(raw);
    if (document.body) document.body.setAttribute('data-ap-path', path);
    return path;
  }
  function readPreviewPath() {
    try {
      const params = new URLSearchParams(window.location.search);
      for (const [k, v] of params) {
        if (k.trim().toLowerCase() === 'ap_path') {
          const p = String(v || '').trim().toLowerCase();
          if (p === 'clinic' || p === 'home') return p;
        }
      }
    } catch (e) {}
    return null;
  }
  function lsGet() {
    try {
      const rec = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (!rec || !rec.code) return null;
      if (Date.now() - (rec.t || 0) > cfg.storageDays * 864e5) {
        localStorage.removeItem(LS_KEY);
        return null;
      }
      return rec;
    } catch (e) {
      return null;
    }
  }
  function lsSet(rec) {
    try {
      if (rec) localStorage.setItem(LS_KEY, JSON.stringify(Object.assign({}, rec, { t: Date.now() })));
      else localStorage.removeItem(LS_KEY);
    } catch (e) {
      /* storage unavailable: cart attributes still work */
    }
  }
  function ssChecks() {
    try {
      return JSON.parse(sessionStorage.getItem(SS_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }
  function ssSaveCheck(code, status, path) {
    try {
      const all = ssChecks();
      all[code] = { status, path: normalizePath(path), t: Date.now() };
      sessionStorage.setItem(SS_KEY, JSON.stringify(all));
    } catch (e) {
      /* ignore */
    }
  }
  function emit(detail) {
    document.dispatchEvent(new CustomEvent('ap-case:change', { detail }));
  }

  /* ---------- validation ---------- */
  /** Resolves to the metaobject entry handle for a normalized code, e.g. AP-DEMO -> 'c-bc0a2185a49e0875'. */
  function handleFor(code) {
    const subtle = window.crypto && window.crypto.subtle;
    if (!subtle || typeof subtle.digest !== 'function' || typeof TextEncoder === 'undefined') {
      return Promise.reject(new Error('crypto.subtle unavailable'));
    }
    return subtle.digest('SHA-256', new TextEncoder().encode(HANDLE_SALT + code)).then((buf) => {
      const hex = Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
      return 'c-' + hex.slice(0, HANDLE_HEX);
    });
  }
  function caseUrl(handle) {
    let url = `${ROOT}pages/${cfg.urlHandle}/${encodeURIComponent(handle)}`;
    const theme = window.Shopify && window.Shopify.theme;
    // Keep theme preview working when testing an unpublished theme.
    if (theme && theme.role && theme.role !== 'main' && theme.id) url += `?preview_theme_id=${theme.id}`;
    return url;
  }

  /**
   * Resolves to { code, status, path, check } where status is one of
   * 'active' | 'closed' | 'unknown' | 'invalid' | 'error',
   * path is 'clinic' | 'home', and check is 'verified' | 'format-only' | 'unverified'.
   */
  const inflight = {};
  function check(rawCode) {
    const code = normalize(rawCode);
    if (!code) return Promise.resolve({ code, status: 'empty' });
    if (!isFormatValid(code)) return Promise.resolve({ code, status: 'invalid' });
    if (cfg.mode === 'format-only') return Promise.resolve({ code, status: 'active', path: 'home', check: 'format-only' });

    const cached = ssChecks()[code];
    if (cached && Date.now() - cached.t < cfg.checkTtlMinutes * 6e4) {
      return Promise.resolve({ code, status: cached.status, path: cached.path || 'home', check: 'verified' });
    }
    if (inflight[code]) return inflight[code];
    inflight[code] = handleFor(code)
      .then((handle) =>
        fetch(caseUrl(handle), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          cache: 'no-store',
        })
      )
      .then((res) => {
        if (res.status === 404) return { status: 'unknown', path: 'home' };
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text().then((html) => {
          const m = html.match(/data-case-status="([a-z-]+)"/i);
          if (!m) throw new Error('marker missing');
          const status = m[1].toLowerCase() === 'active' ? 'active' : 'closed';
          const pm = html.match(/data-landing-path="(clinic|home)"/i);
          const path = pm ? pm[1].toLowerCase() : 'home';
          return { status, path };
        });
      })
      .then((parsed) => {
        const status = parsed.status;
        const path = parsed.path || 'home';
        ssSaveCheck(code, status, path);
        return { code, status, path, check: 'verified' };
      })
      .catch(() => ({ code, status: 'error', path: 'home', check: 'unverified' }))
      .finally(() => {
        delete inflight[code];
      });
    return inflight[code];
  }
  // Usable = may be saved and allows checkout. Network/config errors fall back to format check.
  function usable(result) {
    return result.status === 'active' || result.status === 'error';
  }

  /* ---------- cart ---------- */
  const state = { attrs: {}, note: '', itemCount: 0, loaded: false, ok: {} };
  const NOTE_PREFIX = 'AP case:';

  /**
   * Shopify's packing slip Liquid has no access to order attributes (only order.note), so we also
   * keep ONE marker line in the cart note: "AP case: AP-7K3Q9 | Pet: Biscuit". Any other note text
   * (e.g. a customer note, if cart notes are ever enabled) is preserved.
   */
  function buildNote(code, pet) {
    const keep = String(state.note || '')
      .split(/\r?\n/)
      .filter((l) => l.trim().indexOf(NOTE_PREFIX) !== 0);
    while (keep.length && !keep[keep.length - 1].trim()) keep.pop();
    if (code) keep.push(`${NOTE_PREFIX} ${code}${pet ? ` | Pet: ${pet}` : ''}`);
    return keep.join('\n');
  }

  function readCart() {
    return fetch(`${ROOT}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        state.attrs = cart.attributes || {};
        state.note = cart.note || '';
        state.itemCount = cart.item_count || 0;
        state.loaded = true;
        return cart;
      });
  }
  function updateAttributes(attributes) {
    const code = attributes[ATTR.code] !== undefined ? attributes[ATTR.code] : normalize(state.attrs[ATTR.code]);
    const pet = attributes[ATTR.pet] !== undefined ? attributes[ATTR.pet] : cleanPet(state.attrs[ATTR.pet]);
    const note = buildNote(code, pet);
    return fetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes, note }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then((cart) => {
        state.attrs = cart.attributes || {};
        state.note = cart.note || '';
        state.itemCount = cart.item_count || 0;
        state.loaded = true;
        emit({ type: 'saved', attrs: state.attrs });
        return cart;
      });
  }
  /** Save a validated code. Changing to a different code clears Pet name (it belonged to the old case). */
  function saveCode(code, source, checkResult, pet, path) {
    const attrs = {};
    attrs[ATTR.code] = code;
    attrs[ATTR.source] = source;
    attrs[ATTR.check] = checkResult;
    attrs[ATTR.path] = normalizePath(path);
    const prev = normalize(state.attrs[ATTR.code]);
    if (typeof pet === 'string') attrs[ATTR.pet] = cleanPet(pet);
    else if (prev && prev !== code) attrs[ATTR.pet] = '';
    state.ok[code] = true;
    lsSet({ code, source });
    applyPath(attrs[ATTR.path]);
    return updateAttributes(attrs);
  }
  function clearCode() {
    const attrs = {};
    attrs[ATTR.code] = '';
    attrs[ATTR.source] = '';
    attrs[ATTR.check] = '';
    attrs[ATTR.pet] = '';
    attrs[ATTR.path] = '';
    lsSet(null);
    applyPath('home');
    return updateAttributes(attrs);
  }
  function canCheckout() {
    const code = normalize(state.attrs[ATTR.code]);
    return !!(code && state.ok[code] && cleanPet(state.attrs[ATTR.pet]));
  }

  /* ---------- URL capture ---------- */
  function readParam() {
    const params = new URLSearchParams(window.location.search);
    for (const [k, v] of params) {
      if (k.trim().toLowerCase() === 'case') return v;
    }
    return null;
  }
  function stripParam() {
    const url = new URL(window.location.href);
    const drop = [];
    url.searchParams.forEach((v, k) => {
      if (k.trim().toLowerCase() === 'case') drop.push(k);
    });
    if (!drop.length) return;
    drop.forEach((k) => url.searchParams.delete(k));
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }

  /* ---------- banner ---------- */
  const banner = {
    el: null,
    init() {
      this.el = document.getElementById('ApCaseBanner');
      if (!this.el) return;
      this.codeEl = this.el.querySelector('[data-ap-banner-code]');
      this.msgEl = this.el.querySelector('[data-ap-banner-msg]');
      this.textEl = this.el.querySelector('[data-ap-banner-text]');
      this.form = this.el.querySelector('[data-ap-banner-form]');
      this.input = this.el.querySelector('[data-ap-banner-input]');
      this.changeBtn = this.el.querySelector('[data-ap-banner-change]');
      this.changeBtn &&
        this.changeBtn.addEventListener('click', () => this.toggleForm(this.form.hidden));
      const cancel = this.el.querySelector('[data-ap-banner-cancel]');
      cancel && cancel.addEventListener('click', () => this.toggleForm(false));
      this.form && this.form.addEventListener('submit', (e) => this.onSubmit(e));
    },
    show(code) {
      if (!this.el) return;
      this.el.hidden = !code;
      if (this.codeEl) this.codeEl.textContent = code || '';
      if (this.textEl) this.textEl.hidden = !code;
    },
    message(text) {
      if (!this.el) return;
      if (this.msgEl) renderMsg(this.msgEl, text);
      if (text) this.el.hidden = false;
    },
    toggleForm(open) {
      if (!this.form) return;
      this.form.hidden = !open;
      this.changeBtn && this.changeBtn.setAttribute('aria-expanded', String(open));
      if (open) {
        this.input.value = normalize(state.attrs[ATTR.code]) || '';
        this.input.focus();
      }
    },
    onSubmit(e) {
      e.preventDefault();
      const code = normalize(this.input.value);
      if (!code) {
        clearCode().then(() => {
          this.toggleForm(false);
          this.show('');
          this.message('');
        });
        return;
      }
      this.message(MSG.checking);
      check(code).then((res) => {
        if (!usable(res)) {
          this.message(res.status === 'invalid' ? MSG.format : res.status === 'closed' ? MSG.closed : MSG.unknown);
          return;
        }
        const same = normalize(state.attrs[ATTR.code]) === code;
        const source = same ? state.attrs[ATTR.source] || 'typed' : 'typed';
        saveCode(code, source, res.check, undefined, res.path)
          .then(() => {
            this.toggleForm(false);
            this.show(code);
            this.message(res.status === 'error' ? MSG.unverified : '');
          })
          .catch(() => this.message(MSG.saveError));
      });
    }, };

  /* ---------- checkout guard (all submit paths, (this.result && this.result.path) || (typeof res !== "undefined" && res.path) || "home") ---------- */
  let bypassOnce = false;
  function isCheckoutSubmit(form, submitter) {
    if (submitter && submitter.name === 'checkout') return true;
    const action = (form && form.getAttribute('action')) || '';
    return /\/checkouts?(\/|$|\?)/.test(action);
  }
  function blocked(form) {
    const gate = form && form.id ? document.querySelector(`ap-case-gate[data-form="${form.id}"]`) : null;
    if (gate && typeof gate.prompt === 'function' && !gate.hidden) {
      gate.prompt();
    } else {
      window.location.href = cfg.cartUrl;
    }
  }
  document.addEventListener(
    'submit',
    (e) => {
      if (bypassOnce) {
        bypassOnce = false;
        return;
      }
      if (!isCheckoutSubmit(e.target, e.submitter)) return;
      if (canCheckout()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      blocked(e.target);
    },
    true
  );
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      const link = t.closest('a[href]');
      const express = t.closest('.additional-checkout-buttons, .shopify-payment-button, shopify-accelerated-checkout-cart');
      const isCheckoutLink = link && /\/checkouts?(\/|$|\?)/.test(link.getAttribute('href'));
      if (!isCheckoutLink && !express) return;
      if (canCheckout()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      blocked(document.getElementById('cart') || document.getElementById('CartDrawer-Form'));
    },
    true
  );

  /* ---------- gate element ---------- */
  const drafts = {}; // survives Dawn re-rendering the drawer footer

  class ApCaseGate extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.form;
      this.codeInput = this.querySelector('[data-ap-gate-code]');
      this.petInput = this.querySelector('[data-ap-gate-pet]');
      this.sourceInput = this.querySelector('[data-ap-gate-source]');
      this.checkInput = this.querySelector('[data-ap-gate-check]');
      this.codeMsg = this.querySelector('[data-ap-gate-code-msg]');
      this.petMsg = this.querySelector('[data-ap-gate-pet-msg]');
      this.codeRow = this.querySelector('[data-ap-gate-code-row]');
      this.summary = this.querySelector('[data-ap-gate-summary]');
      this.summaryCode = this.querySelector('[data-ap-gate-summary-code]');
      this.changeBtn = this.querySelector('[data-ap-gate-change]');
      this.result = null;
      this.timer = null;
      this.saving = null;

      const d = drafts[this.formId] || {};
      if (typeof d.code === 'string' && this.codeInput) this.codeInput.value = d.code;
      if (typeof d.pet === 'string' && this.petInput) this.petInput.value = d.pet;

      this.onCodeInput = () => {
        drafts[this.formId] = Object.assign({}, drafts[this.formId], { code: this.codeInput.value });
        this.result = null;
        this.render();
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.validateCode(false), 450);
      };
      this.onPetInput = () => {
        drafts[this.formId] = Object.assign({}, drafts[this.formId], { pet: this.petInput.value });
        if (cleanPet(this.petInput.value)) this.setMsg(this.petMsg, '', '');
        this.render();
        clearTimeout(this.petTimer);
        this.petTimer = setTimeout(() => this.commit(false), 700);
      };
      this.codeInput && this.codeInput.addEventListener('input', this.onCodeInput);
      this.codeInput && this.codeInput.addEventListener('blur', () => this.validateCode(true));
      this.petInput && this.petInput.addEventListener('input', this.onPetInput);
      this.petInput && this.petInput.addEventListener('blur', () => this.commit(false));
      this.changeBtn &&
        this.changeBtn.addEventListener('click', () => {
          this.editing = true;
          this.render();
          this.codeInput && this.codeInput.focus();
        });
      this.onChange = () => this.syncFromCart();
      document.addEventListener('ap-case:change', this.onChange);

      this.syncFromCart();
    }
    disconnectedCallback() {
      document.removeEventListener('ap-case:change', this.onChange);
      clearTimeout(this.timer);
      clearTimeout(this.petTimer);
    }
    buttons() {
      return Array.from(document.querySelectorAll(`[name="checkout"][form="${this.formId}"]`));
    }
    savedCode() {
      return normalize(state.loaded ? state.attrs[ATTR.code] : this.dataset.savedCode);
    }
    syncFromCart() {
      const saved = this.savedCode();
      const savedPet = cleanPet(state.loaded ? state.attrs[ATTR.pet] : this.dataset.savedPet);
      if (saved && this.codeInput && !this.editing && !(drafts[this.formId] || {}).code) this.codeInput.value = saved;
      if (savedPet && this.petInput && !(drafts[this.formId] || {}).pet) this.petInput.value = savedPet;
      if (saved && !this.editing) {
        check(saved).then((res) => {
          if (usable(res)) state.ok[saved] = true;
          this.result = res;
          this.render();
        });
      }
      this.render();
    }
    setMsg(el, text, tone) {
      if (!el) return;
      renderMsg(el, text);
      el.dataset.tone = tone || '';
      el.hidden = !text;
    }
    validateCode(fromBlur) {
      if (!this.codeInput) return Promise.resolve(null);
      const code = normalize(this.codeInput.value);
      if (!code) {
        this.setMsg(this.codeMsg, fromBlur ? MSG.codeMissing : '', 'error');
        return Promise.resolve(null);
      }
      if (!isFormatValid(code)) {
        // Don't nag mid-typing: only flag format once it's long enough or on blur.
        const show = fromBlur || code.length >= 8;
        this.setMsg(this.codeMsg, show ? MSG.format : '', 'error');
        this.codeInput.setAttribute('aria-invalid', show ? 'true' : 'false');
        return Promise.resolve({ code, status: 'invalid' });
      }
      this.setMsg(this.codeMsg, MSG.checking, 'info');
      return check(code).then((res) => {
        if (normalize(this.codeInput.value) !== code) return res; // user kept typing
        this.result = res;
        if (res.status === 'active') this.setMsg(this.codeMsg, MSG.found, 'ok');
        else if (res.status === 'error') this.setMsg(this.codeMsg, MSG.unverified, 'info');
        else if (res.status === 'closed') this.setMsg(this.codeMsg, MSG.closed, 'error');
        else this.setMsg(this.codeMsg, MSG.unknown, 'error');
        this.codeInput.setAttribute('aria-invalid', usable(res) ? 'false' : 'true');
        if (usable(res)) {
          this.codeInput.value = code;
          this.commit(false);
        }
        this.render();
        return res;
      });
    }
    /** Save code + pet name to the cart when both are ready. */
    commit(fromPrompt) {
      const code = this.codeInput ? normalize(this.codeInput.value) : this.savedCode();
      const pet = this.petInput ? cleanPet(this.petInput.value) : '';
      const res = this.result && this.result.code === code ? this.result : null;
      if (!res || !usable(res)) {
        if (fromPrompt) this.validateCode(true);
        return Promise.resolve(false);
      }
      if (!pet) {
        if (fromPrompt) this.setMsg(this.petMsg, MSG.petMissing, 'error');
        return Promise.resolve(false);
      }
      const unchanged =
        normalize(state.attrs[ATTR.code]) === code && cleanPet(state.attrs[ATTR.pet]) === pet && state.ok[code];
      if (unchanged) {
        this.editing = false;
        this.render();
        return Promise.resolve(true);
      }
      const savedCode = normalize(state.attrs[ATTR.code]);
      const source = savedCode === code ? state.attrs[ATTR.source] || 'typed' : 'typed';
      if (this.sourceInput) this.sourceInput.value = source;
      if (this.checkInput) this.checkInput.value = res.check || '';
      this.saving = saveCode(code, source, res.check || 'verified', pet, res.path)
        .then(() => {
          this.editing = false;
          delete drafts[this.formId];
          banner.show(code);
          this.render();
          return true;
        })
        .catch(() => {
          this.setMsg(this.petMsg, MSG.saveError, 'error');
          return false;
        });
      return this.saving;
    }
    prompt() {
      if (typeof this.scrollIntoView === 'function') this.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const codeOk = this.result && usable(this.result);
      if (!codeOk && this.codeInput) {
        this.editing = true;
        this.render();
        this.codeInput.focus();
      } else if (this.petInput) {
        this.petInput.focus();
      }
      this.commit(true);
    }
    render() {
      const ok = canCheckout();
      const saved = this.savedCode();
      const savedOk = !!(saved && state.ok[saved]);
      const showSummary = savedOk && !this.editing;
      if (this.summary) this.summary.hidden = !showSummary;
      if (this.summaryCode) this.summaryCode.textContent = saved;
      if (this.codeRow) this.codeRow.hidden = showSummary;
      this.dataset.state = ok ? 'ok' : 'needs';
      this.buttons().forEach((b) => {
        if (state.itemCount === 0 && state.loaded) return; // Dawn handles empty carts
        b.disabled = !ok;
        b.setAttribute('aria-describedby', this.id || '');
      });
    }
  }

  /* ---------- boot ---------- */
  function applyStored() {
    const attrCode = normalize(state.attrs[ATTR.code]);
    if (attrCode) {
      // Cart already has a code: keep localStorage in sync (e.g. typed on another tab).
      const rec = lsGet();
      if (!rec || rec.code !== attrCode) lsSet({ code: attrCode, source: state.attrs[ATTR.source] || 'typed' });
      return check(attrCode).then((res) => {
        if (usable(res)) state.ok[attrCode] = true;
        if (res.path) {
          applyPath(res.path);
          if (normalizePath(state.attrs[ATTR.path]) !== normalizePath(res.path)) {
            const patch = {};
            patch[ATTR.path] = normalizePath(res.path);
            updateAttributes(patch).catch(() => {});
          }
        } else {
          applyPath(state.attrs[ATTR.path] || 'home');
        }
        banner.show(attrCode);
        emit({ type: 'loaded', attrs: state.attrs });
      });
    }
    const rec = lsGet();
    if (!rec) {
      banner.show('');
      emit({ type: 'loaded', attrs: state.attrs });
      return Promise.resolve();
    }
    // New or recreated cart without attributes: re-apply the stored code.
    return check(rec.code).then((res) => {
      if (!usable(res)) {
        lsSet(null);
        banner.show('');
        emit({ type: 'loaded', attrs: state.attrs });
        return;
      }
      return saveCode(rec.code, rec.source || 'link', res.check, undefined, res.path).then(() => banner.show(rec.code));
    });
  }

  function captureFromUrl(raw) {
    const code = normalize(raw);
    return check(code)
      .then((res) => {
        if (!usable(res)) {
          banner.show(normalize(state.attrs[ATTR.code]));
          if (code) banner.message(MSG.linkProblem);
          return;
        }
        return saveCode(code, 'link', res.check, undefined, res.path).then(() => {
          banner.show(code);
          if (res.status === 'error') banner.message(MSG.unverified);
        });
      })
      .finally(stripParam);
  }

  let recheckTimer = null;
  function boot() {
    banner.init();
    const preview = readPreviewPath();
    if (preview) applyPath(preview);
    else applyPath(state.attrs[ATTR.path] || 'home');
    const raw = readParam();
    readCart()
      .catch(() => null)
      .then(() => {
        if (!preview) applyPath(state.attrs[ATTR.path] || 'home');
        return raw !== null ? captureFromUrl(raw) : applyStored();
      })
      .catch(() => stripParam());

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // eslint-disable-next-line no-undef
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        clearTimeout(recheckTimer);
        recheckTimer = setTimeout(() => {
          readCart()
            .then(() => (normalize(state.attrs[ATTR.code]) ? emit({ type: 'cart', attrs: state.attrs }) : applyStored()))
            .catch(() => {});
        }, 250);
      });
    }
  }

  window.APCase = {
    normalize,
    normalizePath,
    applyPath,
    isFormatValid,
    handleFor,
    check,
    canCheckout,
    state,
    config: cfg,
    clear: clearCode,
    allowNextSubmit() {
      bypassOnce = true;
    },
  };
  if (!customElements.get('ap-case-gate')) customElements.define('ap-case-gate', ApCaseGate);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
